import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import Apis from '@/Apis/_'
import { DataTablePagination, Hospital } from '@/types/_'
import moment from 'moment'

type Mode = 'CREATE' | 'EDIT'

export enum StatusEnum {
  Ativo = 'Ativo',
  Removido = 'Removido',
}

export enum RegraReservaSalaFrequenciaEnum {
  NaoSeRepete = 'NaoSeRepete',
  Diario = 'Diario',
  Semanal = 'Semanal',
  Quinzenal = 'Quinzenal',
  Mensal = 'Mensal',
  Anual = 'Anual',
}

export enum RegraReservaSalaTipoEnum {
  Preferencial = 'Preferencial',
  Bloqueio = 'Bloqueio',
}

export enum SolicitacaoCaraterEnum {
  EletivoAutorizacao = 'EletivoAutorizacao',
  EletivoSemAutorizacao = 'EletivoSemAutorizacao',
  Emergencia = 'Emergencia',
  Urgencia = 'Urgencia',
}

export interface CentroCirurgico {
  id: string,
  hisId: string,
  nome: string,
  status: StatusEnum,
}

export interface Sala {
  id: string,
  nome: string,
  hospitalId: string,
  centroCirurgicoId?: string,
  horaInicio: string,
  horaFim: string,
  tempoPreparacao: number,
  hisId?: string,
  status: StatusEnum,
}

export type Responsavel = 'Medicos' |'Especialidades'

export enum TipoResponsavelReservaSala {
  ReservaMedico = 'ReservaMedico',
  ReservaEspecialidade = 'ReservaEspecialidade',
  ReservaConvenios = 'ReservaConvenios', // Caso não haja médico ou especialidade...
  ReservaBloqueio = 'ReservaBloqueio',
}

export interface RegraReservaSala {
  id: string,
  nome: string,
  hospitalId: string,
  salaId: string,
  horaInicio: string,
  horaFim: string,
  dataInicio: string,
  dataFim: string,
  tipo: RegraReservaSalaTipoEnum,
  solicitacaoCarater?: SolicitacaoCaraterEnum,
  vinculoComMedicos?: boolean,
  vinculoComEspecialidades?: boolean,
  vinculoComConvenios?: boolean,
  frequencia: RegraReservaSalaFrequenciaEnum,
  status: StatusEnum,
}

export interface RegraReservaSalaUpsert {
  id?: string,
  nome: string,
  salaId: string,
  hospitalId: string,
  status: StatusEnum,
  horaInicio: string,
  horaFim: string,
  tipo: RegraReservaSalaTipoEnum,
  solicitacaoCarater?: SolicitacaoCaraterEnum
  ehObrigatorioAtenderARegra: boolean,
  perdaDeReservaEmDias: number,
  observacoes?: string,
  convenios?: string[],
  medicos?: string[],
  especialidades?: string[],
  recorrencia: Recorrencia,
}

export interface Recorrencia {
  frequencia: RegraReservaSalaFrequenciaEnum,
  intervalo: number,
  diaDaSemana: any[],
  semanasDoMes?: number[],
  diaDoMes?: number,
  mes?: number,
  dataInicio: string,
  dataFim: string,
}

/**
 * NaoSeRepete
 * Intervalo: 0
 * diaDaSemana: undefined,
 * diaDoMes: undefined
 * mes: undefined,
 * dataInicio: 2024-12-11,
 * dataFim: 2024-12-31,
 */

/**
 * Diario
 * Intervalo: Número de dias para repetir a regra PADRAO = 1
 * diaDaSemana: undefined,
 * diaDoMes: undefined,
 * mes: undefined,
 * dataInicio: 2024-12-11,
 * dataFim: 2024-12-31,
 */

/**
 * Semanal
 * Intervalo: Número de semanas para repetir a regra PADRAO = 1
 * diaDaSemana: 0-6,
 * semanasDoMes: [] (todas as semanas) ou 1-5, sem repetição — 6 nunca gera ocorrência (bloco de 7 dias corridos a partir do dia 1 do mês)
 * diaDoMes: undefined,
 * mes: undefined,
 * dataInicio: 2024-12-11,
 * dataFim: 2024-12-31,
 */

/**
 * Quinzenal
 * Intervalo: fixo em 1 (a cadência real é de 15 dias corridos, não configurável pelo campo intervalo)
 * diaDaSemana: undefined — ignorado pelo backend nesta frequência (a cada 15 dias o dia da semana deriva)
 * semanasDoMes: [],
 * diaDoMes: undefined,
 * mes: undefined,
 * dataInicio: 2024-12-11,
 * dataFim: 2024-12-31,
 */

/**
 * Mensal
 * Intervalo: Número de meses para repetir a regra PADRAO = 1
 * diaDaSemana: undefined,
 * diaDoMes: 1-31,
 * mes: undefined,
 * dataInicio: 2024-12-11,
 * dataFim: 2024-12-31,
 */

/**
 * Anual
 * Intervalo: Número de anos para repetir a regra PADRAO = 1
 * diaDaSemana: undefined,
 * diaDoMes: undefined,
 * mes: 1-12,
 * dataInicio: 2024-12-11,
 * dataFim: 2026-12-31,
 */

@Module({ namespaced: true, name: 'BackofficeSalasEReservasStore' })
export default class BackofficeSalasEReservasStore extends VuexModule {
  public mode: Mode = 'CREATE'

  @Mutation
  setMode(mode: Mode) {
    this.mode = mode
  }

  public modal: any = {
    agendarSala: false,
    frequencia: false,
  }

  @Mutation
  setModal(modal: any) {
    this.modal[modal.name] = modal.value
  }

  public searchRegrasReserva: string = ''

  @Mutation
  setSearchRegrasReserva(value: string) {
    this.searchRegrasReserva = value
  }

  @Mutation
  clearSearchRegrasReserva() {
    this.searchRegrasReserva = ''
  }

  public searchSalaId: string = ''

  @Mutation
  setSearchSalaId(value: string) {
    this.searchSalaId = value
  }

  @Mutation
  clearSearchSalaId() {
    this.searchSalaId = ''
  }


  /** Hospitais, Centros cirúrgicos, Salas, Operadoras, Médicos e CBOs... */
  public hospitalId: string = ''

  @Mutation
  setHospitalId(value: string) {
    this.hospitalId = value
  }

  @Mutation
  clearHospitalId() {
    this.hospitalId = ''
  }

  public centrosCirurgicos: CentroCirurgico[] = []

  @Mutation
  setCentrosCirurgicos(value: CentroCirurgico[]) {
    this.centrosCirurgicos = value
  }

  @Mutation
  clearCentrosCirurgicos() {
    this.centrosCirurgicos = []
  }

  @Action
  async getAllCentrosCirurgicosByHospital() {
    try {
      const request = await Apis.Agendamento.SuporteApi.getAllCentrosCirurgicosByHospital({ hospitalId: this.hospitalId, pagina: 1, qtdPorPagina: 100, retornaSomenteAtivos: true })
      if (request.success) this.context.commit('setCentrosCirurgicos', request.data.centrosCirurgicos || [])
      return request
    } catch (error) {
      this.context.commit('setCentrosCirurgicos', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  public salas: Sala[] = []

  @Mutation
  setSalas(value: Sala[]) {
    this.salas = value
  }

  @Mutation
  clearSalas() {
    this.salas = []
  }

  @Action
  async getAllSalasCirurgicasByHospital() {
    try {
      const request = await Apis.Agendamento.SuporteApi.getAllSalasCirurgicasByHospital({ hospitalId: this.hospitalId, pagina: 1, qtdPorPagina: 100, retornaSomenteAtivos: true })
      if (request.success) {
        this.context.commit('setSalas', request.data.salasCirurgicas || [])
        this.context.commit('setSalasAgrupadas', request.data.salasCirurgicas || [])
      }
      return request
    } catch (error) {
      this.context.commit('setSalas', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  public salasAgrupadas: any[] = []

  @Mutation
  setSalasAgrupadas(salas: Sala[]) {
    if (this.centrosCirurgicos.length > 0) {
      const group: any[] = this.centrosCirurgicos.reduce((acc: any[], centro: CentroCirurgico) => {
        acc.push({ header: centro.nome })
        const centroSalas = salas.filter((sala: Sala) => sala.centroCirurgicoId === centro.id).sort((a: Sala, b: Sala) => a.nome.localeCompare(b.nome))
        centroSalas.forEach((sala: Sala) => {
          acc.push({ salaId: sala.id, nome: sala.nome, group: centro.nome })
        })
        acc.push({ divider: true })
        return acc
      }, [])
      group.pop()
      this.salasAgrupadas = [...group]
    } else {
      this.salasAgrupadas = salas.map((sala: Sala) => ({ salaId: sala.id, nome: sala.nome })).sort((a: any, b: any) => a.nome.localeCompare(b.nome))
    }
  }

  public operadoras: any[] = []

  @Mutation
  setOperadoras(value: any[]) {
    this.operadoras = value
  }

  @Mutation
  clearOperadoras() {
    this.operadoras = []
  }

  @Mutation
  removeOperadora(id: string) {
    this.regraReservaSala.convenios!.splice(this.regraReservaSala.convenios!.findIndex((convenioId: string) => convenioId === id), 1)
  }

  @Action
  async getAllConvenios() {
    try {
      const request = await Apis.Agendamento.SuporteApi.getAllConvenio(this.hospitalId)
      if (request.success) this.context.commit('setOperadoras', request.data || [])
      return request
    } catch (error) {
      this.context.commit('setOperadoras', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  public cbos: any[] = []

  @Mutation
  setCbos(value: any[]) {
    this.cbos = value
  }

  @Mutation
  clearCbos() {
    this.cbos = []
  }

  @Mutation
  removeEspecialidade(id: string) {
    this.regraReservaSala.especialidades!.splice(this.regraReservaSala.especialidades!.findIndex((especialidadeId: string) => especialidadeId === id), 1)
  }

  @Action
  async getCbosAll() {
    try {
      const request = await Apis.Cadastro.CadastroApi.getCbosAll()
      if (request.success) this.context.commit('setCbos', request.data || [])
      return request
    } catch (error) {
      this.context.commit('setCbos', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  public medicos: any[] = []

  @Mutation
  setMedicos(value: any[]) {
    this.medicos = value
  }

  @Mutation
  clearMedicos() {
    this.medicos = []
  }

  @Mutation
  removeMedico(id: string) {
    this.regraReservaSala.medicos!.splice(this.regraReservaSala.medicos!.findIndex((medicoId: string) => medicoId === id), 1)
  }

  @Action
  async getAllMedicoByUsuarioId(userId: string) {
    try {
      const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.getAllMedicoByUsuarioId(userId)
      if (request.success) this.context.commit('setMedicos', request.data || [])
      return request
    } catch (error) {
      this.context.commit('setMedicos', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  /** Regras de reserva de sala */
  private paginationRegraReservaSalaDefault: DataTablePagination = {
    descending: false,
    page: 1,
    rowsPerPage: 25,
    sortBy: null,
    totalItems: 0,
    rowsPerPageItems: [25, 50, 100, 200],
  }

  public paginationRegraReservaSala: DataTablePagination = { ...this.paginationRegraReservaSalaDefault }

  @Mutation
  setPaginationRegraReservaSala(value: DataTablePagination) {
    this.paginationRegraReservaSala = value
  }

  get paginationDefault() {
    return this.paginationRegraReservaSalaDefault
  }

  public regrasReservaSala: RegraReservaSala[] = []

  @Mutation
  setRegrasReservaSala(value: RegraReservaSala[]) {
    this.regrasReservaSala = value
  }

  @Mutation
  clearRegrasReservaSala() {
    this.regrasReservaSala = []
  }

  @Action
  async getAllRegraReservaSala(pagination: DataTablePagination) {
    try {
      const params: any = {
        hospitalId: this.hospitalId,
        pagina: pagination.page || this.paginationRegraReservaSalaDefault.page,
        qtdPorPagina: pagination.rowsPerPage || this.paginationRegraReservaSalaDefault.rowsPerPage,
        searchText: this.searchRegrasReserva,
        salaId: this.searchSalaId,
      }

      const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.getAllRegraReservaSala(params)

      if (request.success) {
        this.context.commit('setRegrasReservaSala', request.data.regrasDeReservaDeSala as RegraReservaSala[] || [])
        this.context.commit('setPaginationRegraReservaSala', {
          descending: false,
          page: pagination.page || this.paginationRegraReservaSalaDefault.page,
          rowsPerPage: pagination.rowsPerPage || this.paginationRegraReservaSalaDefault.rowsPerPage,
          sortBy: null,
          totalItems: request.data.total,
          rowsPerPageItems: this.paginationRegraReservaSalaDefault.rowsPerPageItems,
        })
      }

      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  private recorrenciaDefault: Recorrencia = {
    frequencia: RegraReservaSalaFrequenciaEnum.NaoSeRepete,
    intervalo: 0,
    diaDaSemana: [],
    semanasDoMes: [],
    diaDoMes: undefined,
    mes: undefined,
    dataInicio: new Date().toISOString().slice(0, 10),
    dataFim: new Date().toISOString().slice(0, 10),
  }

  private regraReservaSalaDefault: RegraReservaSalaUpsert = {
    id: undefined,
    nome: '',
    salaId: '',
    hospitalId: this.hospitalId,
    status: StatusEnum.Ativo,
    horaInicio: '08:00',
    horaFim: '12:00',
    tipo: RegraReservaSalaTipoEnum.Preferencial,
    solicitacaoCarater: undefined,
    ehObrigatorioAtenderARegra: false,
    perdaDeReservaEmDias: 0,
    observacoes: '',
    convenios: [],
    medicos: [],
    especialidades: [],
    recorrencia: { ...this.recorrenciaDefault },
  }

  public regraReservaSala: RegraReservaSalaUpsert = { ...this.regraReservaSalaDefault }

  @Mutation
  setRegraReservaSala(value: RegraReservaSalaUpsert) {
    this.regraReservaSala = value

    // Formatação de datas para adequação ao componente...
    const { horaInicio, horaFim } = this.regraReservaSala
    const { dataInicio, dataFim, diaDaSemana } = this.regraReservaSala.recorrencia
    if (dataInicio) this.regraReservaSala.recorrencia.dataInicio = moment(dataInicio).format('YYYY-MM-DD')
    if (dataFim) this.regraReservaSala.recorrencia.dataFim = moment(dataFim).format('YYYY-MM-DD')
    if (horaInicio) this.regraReservaSala.horaInicio = moment(horaInicio, 'HH:mm:ss').format('HH:mm')
    if (horaFim) this.regraReservaSala.horaFim = moment(horaFim, 'HH:mm:ss').format('HH:mm')

    // @ts-ignore
    this.regraReservaSala.recorrencia.diaDaSemana = !diaDaSemana ? [] : diaDaSemana.split('').map(Number)

    if (!Array.isArray(this.regraReservaSala.recorrencia.semanasDoMes)) {
      this.regraReservaSala.recorrencia.semanasDoMes = []
    }
  }

  @Mutation
  clearRegraReservaSala() {
    this.regraReservaSala = { ...this.regraReservaSalaDefault }
    this.regraReservaSala.recorrencia = { ...this.recorrenciaDefault }
  }

  @Mutation
  setRegraReservaSalaField({ field, value }: { field: string, value: any }) {
    this.regraReservaSala[field] = value
  }

  @Mutation
  setRegraReservaSalaRecorrencia({ field, value }: { field: string, value: any }) {
    this.regraReservaSala.recorrencia[field] = value
  }

  @Action
  async getRegraReservaSalaById(id: string) {
    try {
      const params: any = { hospitalId: this.hospitalId, regraReservaSalaId: id }
      const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.getRegraReservaSalaById(params)
      if (request.success) {
        this.context.commit('setRegraReservaSala', request.data as RegraReservaSalaUpsert)

        // Mudança do seletor de responsabilidade para 'Especialidades', caso a regra tenha especialidades...
        if (this.regraReservaSala.especialidades && this.regraReservaSala.especialidades.length > 0) {
          this.context.commit('setSelectorMedicosEspecialidades', 'Especialidades')
        }
      }
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  private async persistRegraReserva(status?: StatusEnum) {
    const params = { ...this.regraReservaSala }
    if (status) params.status = status

    Object.entries(params).forEach(([key, value]) => {
      if (['', null, undefined].includes(value as any)) delete params[key]
    })

    params.horaInicio = `${params.horaInicio}:00`
    params.horaFim = `${params.horaFim}:00`

    if ('recorrencia' in params) {
      // @ts-ignore
      params.recorrencia.diaDaSemana = params.recorrencia.diaDaSemana!.length ? params.recorrencia.diaDaSemana!.join('') : ''

      // semanasDoMes só é aceito pelo backend na frequência Semanal — nas demais, o campo nem deve ser enviado.
      if (params.recorrencia.frequencia !== RegraReservaSalaFrequenciaEnum.Semanal) {
        delete params.recorrencia.semanasDoMes
      }
    }

    const request = await Apis.Agendamento.AgendamentoMedicoBackofficeApi.upsertRegraReserva({ ...params, hospitalId: this.hospitalId })
    if (request.success) this.context.dispatch('getAllRegraReservaSala', this.paginationRegraReservaSala)
    return request
  }

  @Action
  async upsertRegraReserva() {
    try {
      return await this.context.dispatch('persistRegraReserva')
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async updateRegraReservaStatus(status: StatusEnum) {
    try {
      return await this.context.dispatch('persistRegraReserva', status)
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  public selectorMedicosEspecialidades: Responsavel = 'Medicos'

  @Mutation
  setSelectorMedicosEspecialidades(value: Responsavel) {
    this.selectorMedicosEspecialidades = value
  }

  get mininumDateForDataFim() {
    if (this.regraReservaSala.recorrencia.dataInicio) return moment(this.regraReservaSala.recorrencia.dataInicio).format('YYYY-MM-DD')
    return new Date().toISOString().substring(0, 10)
  }

  public recurrencyDescription: string = ''

  @Mutation
  setRecurrencyDescription(value: string) {
    this.recurrencyDescription = value
  }
}
